import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

export default function Gatekeeper({ 
  children, 
  requireFounder = false,
  requireAdmin = false, 
  requiredPermission = null 
}) {
  const { 
    currentUser, 
    isFounder, 
    isClientOwner, 
    isAdmin, 
    hasPermission,
    loading: authLoading 
  } = useAuth();
  
  const { loading: tenantLoading } = useTenant(); 
  const location = useLocation();

  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // --- REAL-TIME COMPLIANCE LISTENER ---
  useEffect(() => {
    if (!currentUser) {
      setUserLoading(false);
      return;
    }

    // Creates a live wire to the user's profile to monitor FTL status and DB data
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
      setUserLoading(false);
    }, (error) => {
      console.error("Gatekeeper failed to verify user profile:", error);
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 1. Wait for ALL layers of authentication and database resolution
  if (authLoading || tenantLoading || userLoading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900 text-white font-sans">
        <div className="flex flex-col items-center space-y-6">
          <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
          <div className="text-center">
            <p className="text-xs font-black uppercase tracking-widest mb-1 text-emerald-400">
              {authLoading ? "Authenticating Session..." : tenantLoading ? "Securing Corporate Workspace..." : "Verifying Compliance..."}
            </p>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">eeKoSystem Vault Exchange</p>
          </div>
        </div>
      </div>
    );
  }

  // 2. Reject unauthenticated users instantly
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. --- FTL BOUNCER LOGIC (The Switch Flip) ---
  const isFtlComplete = userData?.ftlCompleted === true;
  const isTryingToAccessFtl = location.pathname === '/ftl';

  // If they haven't completed FTL and they aren't on the FTL page, trap them.
  if (!isFtlComplete && !isTryingToAccessFtl) {
    return <Navigate to="/ftl" replace />;
  }

  // If they HAVE completed FTL but try to go back to the FTL page, push them to the dashboard.
  if (isFtlComplete && isTryingToAccessFtl) {
    return <Navigate to="/" replace />;
  }

  // 4. Enforce Founder-level clearance (God Mode)
  if (requireFounder && !isFounder) {
    return <AccessDeniedMessage message="You lack the executive cryptographic permissions required to access the Master Control Plane." />;
  }

  // 5. Enforce Admin-level clearance (Tenant Owners & Admins)
  if (requireAdmin && !(isFounder || isClientOwner || isAdmin)) {
    return <AccessDeniedMessage message="Administrative clearance is required to view this workspace." />;
  }

  // 6. Enforce Granular PBAC Permissions (Apex OS Parity)
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <AccessDeniedMessage message={`Access Denied. Missing required security policy: [${requiredPermission}].`} />;
  }

  // 7. Access Granted! Render the requested route.
  return children;
}

// Extracted Denied Message Component for cleaner rendering
function AccessDeniedMessage({ message }) {
  return (
    <div className="flex items-center justify-center h-[80vh] bg-slate-50 animate-in fade-in duration-300">
      <div className="bg-white border border-rose-200 p-10 rounded-sm shadow-xl text-center max-w-md">
        <div className="w-16 h-16 bg-rose-50 text-rose-600 border border-rose-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fa-solid fa-hand text-2xl"></i>
        </div>
        <h2 className="text-slate-900 font-black uppercase tracking-widest text-xl mb-2">Clearance Denied</h2>
        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest leading-relaxed">
          {message}
        </p>
      </div>
    </div>
  );
}